import React, { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../lib/lang";
import { Mail, Loader2, Check } from "lucide-react";

const COPY = {
  title: { en: "Stay informed", kar: "သ့ၣ်ညါတၢ်ဂ့ၢ်အိၣ်ဒၣ်", my: "သတင်းအချက်အလက်ရရှိနေပါစေ" },
  desc: {
    en: "Receive official press releases and field updates directly in your inbox.",
    kar: "နုာ်ဘၣ်တၢ်ကစီၣ်လၢအံမ့ၣ်လ်",
    my: "တရားဝင် သတင်းထုတ်ပြန်ချက်များနှင့် နယ်မြေအခြေအနေ မွမ်းမံချက်များကို တိုက်ရိုက်လက်ခံရရှိပါ။",
  },
  placeholder: { en: "your@email.com", kar: "နတၢ်အံမ့ၣ်လ်", my: "သင့်အီးမေးလ်" },
  cta: { en: "Subscribe", kar: "နုာ်လီၤ", my: "စာရင်းပေး" },
  thanks: { en: "You're subscribed.", kar: "ဆၢဂ့ၤ", my: "စာရင်းပေးပြီးပါပြီ" },
};

export default function NewsletterSignup({ variant = "light" }) {
  const { lang } = useLang();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/newsletter/subscribe", { email });
      setDone(true);
      setEmail("");
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Could not subscribe");
    } finally {
      setBusy(false);
    }
  }

  const dark = variant === "dark";
  const t = (k) => COPY[k][lang] || COPY[k].en;

  return (
    <section
      className={`${dark ? "bg-slate-900 text-white" : "bg-slate-50 border border-slate-200"} py-16`}
      data-testid="newsletter-section"
    >
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        <div>
          <div className={`text-xs uppercase tracking-[0.3em] font-semibold mb-3 ${dark ? "text-red-500" : "text-red-600"}`}>
            <Mail className="inline w-3 h-3 mr-1" /> Newsletter
          </div>
          <h2 className="font-display text-4xl sm:text-5xl mb-3">{t("title")}</h2>
          <p className={`max-w-md ${dark ? "text-slate-300" : "text-slate-600"}`}>{t("desc")}</p>
        </div>
        {done ? (
          <div className={`flex items-center gap-3 ${dark ? "text-white" : "text-slate-900"}`} data-testid="newsletter-success">
            <div className="w-12 h-12 bg-red-600 text-white flex items-center justify-center">
              <Check className="w-6 h-6" />
            </div>
            <div className="font-display text-2xl">{t("thanks")}</div>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3" data-testid="newsletter-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("placeholder")}
              data-testid="newsletter-email"
              className={`flex-1 border px-4 py-3 text-sm focus:outline-none ${
                dark
                  ? "bg-slate-800 border-slate-700 text-white placeholder-slate-500 focus:border-red-500"
                  : "bg-white border-slate-300 focus:border-slate-900"
              }`}
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="newsletter-submit"
              className={`px-6 py-3 text-sm uppercase tracking-[0.2em] font-semibold transition flex items-center justify-center gap-2 disabled:opacity-60 ${
                dark ? "bg-red-600 text-white hover:bg-red-700" : "bg-slate-900 text-white hover:bg-red-600"
              }`}
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {t("cta")}
            </button>
            {error && (
              <div className="text-sm text-red-500" data-testid="newsletter-error">{error}</div>
            )}
          </form>
        )}
      </div>
    </section>
  );
}
